import type {
  ConnectionState,
  LineAmountType,
  LogEntry,
  QueueEntry,
  ReferenceData,
  TenantConnection,
} from '@domain';
import * as fixtures from './dev/fixtures.js';
import { EMPTY_PRESENTATION, type Presentation } from './screens/printTypes.js';

export type { Presentation };
import type { ListFixture } from './types.js';

/**
 * Everything the screens know about the outside world.
 *
 * Note what is absent: a Xero tenant id. The browser is never told one, so it
 * cannot send one, which is the simplest possible guarantee that a client
 * cannot choose whose books to look at. Companies are referred to by our own
 * internal `orgId`, and the server re-checks entitlement on every request.
 */

export type DataSource = 'api' | 'local-fixtures';

export interface Organisation {
  orgId: number;
  name: string;
  nameAr: string | null;
  currency: string;
  role: 'client_user' | 'client_admin' | 'cfoai_staff' | 'cfoai_admin';
  /**
   * Connection health, so the switcher can show which clients need attention.
   *
   * Optional because it has to be: an organisation whose authorisation row is
   * missing or unreadable has no health to report, and claiming otherwise is
   * how the switcher crashed the whole page.
   */
  state?: ConnectionState;
  /**
   * Does this company have the Training section.
   *
   * The practice decides it per client, and it defaults to off. Optional
   * so a session served before the column existed reads as absent rather
   * than throwing — and absent is treated as off, which withholds the
   * section instead of offering one whose routes would refuse.
   */
  trainingEnabled?: boolean;
  /** Is the Xero `projects` scope asked for on this company's connection. */
  xeroProjectsEnabled?: boolean;
  /**
   * How this company's documents state their amounts, before anyone
   * touches the form.
   *
   * Optional, so a session served before the column existed reads as
   * absent — and absent means Xero's own 'Exclusive', which is what every
   * form did until now.
   */
  defaultAmountsAre?: LineAmountType;
}

export interface Session {
  user: { email: string | null; displayName: string | null };
  organisations: Organisation[];
  activeOrgId: number | null;
  source: DataSource;
}

/**
 * The session ended while the page was open.
 *
 * Worth its own type because it is not a failure: nothing is broken, the person
 * simply needs to sign in again, and telling them that is a different screen
 * from telling them something went wrong.
 */
/** Xero refused the document, and said why. */
export class XeroRejectedError extends Error {
  constructor(readonly messages: string[]) {
    super(messages.join(' | '));
    this.name = 'XeroRejectedError';
  }
}

export class NotSignedInError extends Error {
  constructor() {
    super('Not signed in');
    this.name = 'NotSignedInError';
  }
}

async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(path, { headers: { Accept: 'application/json' } });
  if (response.status === 401 || response.status === 403) throw new NotSignedInError();
  if (!response.ok) throw new Error(`${path} returned ${response.status}`);
  return (await response.json()) as T;
}

/**
 * Local development has no Entra External ID to sign in against and no Xero app
 * registered, so the API cannot answer. Rather than show an empty shell, the
 * screens run on invented data — and say so, visibly, so nobody mistakes a
 * demonstration for a client's real figures.
 */
function fixtureSession(): Session {
  return {
    user: { email: 'a.bacha@cfoai.cpa', displayName: 'A. Bacha' },
    organisations: fixtures.TENANTS.map((t, index) => ({
      // Not 1-based. Real org ids start wherever the database got to,
      // and a fixture whose first company happens to match the client's
      // default id is a fixture that cannot reproduce the first visit.
      orgId: index + 7,
      name: t.name,
      nameAr: null,
      currency: 'QAR',
      /*
        Not all the same. A demo where every company makes you the practice
        cannot show what a client's own admin sees, and that is exactly the
        screen most likely to be got wrong — the last company is theirs.
      */
      role: (index === fixtures.TENANTS.length - 1 ? 'client_admin' : 'cfoai_admin') as
        | 'cfoai_admin'
        | 'client_admin',
      state: t.state,
      /*
        Only the first company has Training, because that is the real
        arrangement: it was sold to one client. A demo where every
        company has it could never show the sidebar leaving it out, which
        is the behaviour most likely to be got wrong.
      */
      trainingEnabled: index === 0,
      xeroProjectsEnabled: false,
      /*
        Only the first company is set to No tax, because that is the real
        arrangement: the practice sets it per client. A demo where every
        company had it could never show the one thing most likely to be
        got wrong — that switching company changes the basis the forms
        start on.
      */
      defaultAmountsAre: (index === 0 ? 'NoTax' : 'Exclusive') as LineAmountType,
    })),
    /*
      Nothing active, which is what a real first visit looks like: the active
      company lives in a cookie, and a new browser or a new address has none.
      Starting the demo on a dashboard hid a bug where the picker never
      appeared at all, so it starts where everyone else starts.
    */
    activeOrgId: null,
    source: 'local-fixtures',
  };
}

/**
 * Who is signed in, and what they may reach.
 *
 * The invented data is only ever used when the API cannot be reached AND this
 * is a development build. It must never appear on the deployed site: a signed-in
 * client with no permissions yet would otherwise be shown a screen full of
 * invented companies and figures and have no way to tell. An empty list is a
 * real answer and is reported as one.
 */
export async function loadSession(): Promise<Session> {
  try {
    const data = await getJson<Omit<Session, 'source'>>('/api/me');
    return { ...data, source: 'api' };
  } catch (error) {
    // A lost session is a real answer and must reach the caller unchanged, so
    // it can be shown as "you are signed out" rather than swallowed by demo data.
    if (error instanceof NotSignedInError) throw error;
    if (import.meta.env.DEV) return fixtureSession();
    throw error;
  }
}

export async function setActiveOrganisation(orgId: number, source: DataSource): Promise<void> {
  if (import.meta.env.DEV && source === 'local-fixtures') return;
  await fetch('/api/organisations/active', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ orgId }),
  });
}

export interface ReferenceBundle {
  reference: ReferenceData;
  knownReferences: string[];
}

export async function loadReference(source: DataSource): Promise<ReferenceBundle> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    return { reference: fixtures.REFERENCE, knownReferences: fixtures.KNOWN_REFERENCES };
  }
  return getJson<ReferenceBundle>('/api/reference');
}

export async function loadList(key: string, source: DataSource): Promise<ListFixture> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    const list = fixtures.LISTS[key];
    if (!list) throw new Error(`Unknown list ${key}`);
    return list as ListFixture;
  }
  return getJson<ListFixture>(`/api/lists/${key}`);
}

export async function loadQueue(source: DataSource): Promise<QueueEntry[]> {
  return import.meta.env.DEV && source === 'local-fixtures' ? fixtures.QUEUE : getJson<QueueEntry[]>('/api/queue');
}

export async function loadLog(source: DataSource): Promise<LogEntry[]> {
  return import.meta.env.DEV && source === 'local-fixtures' ? fixtures.LOG : getJson<LogEntry[]>('/api/log');
}

/** Connection health for the admin screen. */
export async function loadConnections(source: DataSource): Promise<TenantConnection[]> {
  return import.meta.env.DEV && source === 'local-fixtures'
    ? fixtures.TENANTS
    : getJson<TenantConnection[]>('/api/connections');
}

export interface PushResult {
  status: number;
  idempotencyKey: string;
  endpoint: string;
  message?: string;
  /** Xero's own response. Carries the id of whatever was just created. */
  data?: unknown;
}

/**
 * The ContactID Xero assigned to a contact we just created.
 *
 * Xero answers a create with the full record, so the id is there to be used —
 * which is what lets someone add a contact mid-invoice and find it already
 * chosen when they get back, rather than having to hunt for it.
 */
/** The code Xero assigned to an item we just created. */
export function createdItemCode(data: unknown): string | null {
  const items = (data as { Items?: { Code?: string }[] } | null)?.Items;
  return items?.[0]?.Code ?? null;
}

export function createdContactId(data: unknown): string | null {
  const contacts = (data as { Contacts?: { ContactID?: string }[] } | null)?.Contacts;
  return contacts?.[0]?.ContactID ?? null;
}

/**
 * The invoice or bill Xero just created, as far as paying it is concerned.
 *
 * Xero answers a create with the whole record, so the id is there to be
 * used. Taking it is what lets somebody who has just filed a bill settle it
 * without going to find it again in a list of everything outstanding —
 * which is the step that gets put off, and then forgotten.
 *
 * Null for anything that is not an invoice or a bill, and null for one with
 * nothing left to pay: a zero-total document is complete the moment it
 * exists, and offering to pay it would be offering nonsense.
 */
export function createdInvoice(
  data: unknown,
): { id: string; number: string; amountDue: number } | null {
  const invoices = (
    data as {
      Invoices?: {
        InvoiceID?: string;
        InvoiceNumber?: string;
        AmountDue?: number;
        Total?: number;
      }[];
    } | null
  )?.Invoices;

  const first = invoices?.[0];
  if (!first?.InvoiceID) return null;

  /*
    AmountDue, or the total if Xero did not send one.

    On a document created a second ago these are the same number — nothing
    has been paid against it yet. Both are read so that the offer to settle
    it does not rest on one field: Xero documents AmountDue on a create and
    returns it, but a missing field would otherwise read as "nothing owing"
    and quietly skip the question.
  */
  const owing =
    typeof first.AmountDue === 'number' ? first.AmountDue
    : typeof first.Total === 'number' ? first.Total
    : 0;

  // Nothing to pay, so nothing to ask. A zero-total document is finished
  // the moment it exists.
  if (owing <= 0) return null;

  return { id: first.InvoiceID, number: first.InvoiceNumber ?? '', amountDue: owing };
}

export async function pushDocument(body: unknown, source: DataSource): Promise<PushResult> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    const kind = (body as { kind?: string } | null)?.kind;
    return {
      status: 200,
      idempotencyKey: 'demo-0000-0000',
      endpoint: 'PUT /Invoices',
      message: 'Demonstration only — nothing was sent to Xero.',
      /*
        Shaped like Xero's own answer, and only for the two kinds that
        have something to pay. Without it the demo stops at the green
        banner and the question that now follows a filing — was it paid —
        could never be seen before it was deployed.

        The id is one the payment fixtures already know about, so the
        invoice arrives chosen rather than merely named.
      */
      data:
        kind === 'invoice' || kind === 'bill'
          ? {
              Invoices: [
                {
                  InvoiceID: fixtures.PAYMENT_OPTIONS.invoices[0]!.id,
                  InvoiceNumber: fixtures.PAYMENT_OPTIONS.invoices[0]!.number,
                  AmountDue: fixtures.PAYMENT_OPTIONS.invoices[0]!.amountDue,
                },
              ],
            }
          : undefined,
    };
  }

  const response = await fetch('/api/documents/push', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const text = await response.text();
    try {
      const parsed = JSON.parse(text) as { error?: string; messages?: string[] };
      if (parsed.error === 'xero_rejected' && parsed.messages?.length) {
        throw new XeroRejectedError(parsed.messages);
      }
      throw new Error(parsed.error ?? text);
    } catch (error) {
      if (error instanceof XeroRejectedError) throw error;
      throw new Error(text.slice(0, 300));
    }
  }

  return (await response.json()) as PushResult;
}


/* ---------------------------------------------------------------------------
   Who can reach a company.
--------------------------------------------------------------------------- */

export type InvitableRole = Organisation["role"];

export interface Person {
  userId: number;
  email: string | null;
  displayName: string | null;
  role: InvitableRole;
  since: string;
  lastSeen: string | null;
  /** Whether we made their sign-in and can therefore set a new password on it. */
  canSetPassword: boolean;
}

/**
 * Whether a sign-in exists for an invited address.
 *
 * null means nobody asked — the invitation predates account creation, or this
 * deployment has no directory credentials. Not the same as "no account", and
 * the screen must not say it is.
 */
export type AccountState = 'created' | 'already_existed' | 'refused' | 'unavailable' | null;

export interface PendingInvitation {
  invitationId: number;
  email: string;
  displayName: string | null;
  role: InvitableRole;
  invitedAt: string;
  accountState: AccountState;
  accountDetail: string | null;
  /** Whether we made their sign-in and can therefore set a new password on it. */
  canSetPassword: boolean;
}

export interface PeopleResponse {
  organisation: string;
  grantableRoles: InvitableRole[];
  /** Whether this deployment makes the sign-in itself, or the person must. */
  createsSignIn: boolean;
  /** A client admin manages their own colleagues, not their accountants. */
  canRemoveFirmMembers: boolean;
  members: Person[];
  invited: PendingInvitation[];
}

/* ---------------------------------------------------------------------------
   What this company has actually been doing.
--------------------------------------------------------------------------- */

export interface DashboardCounts {
  pushedToday: number;
  pushedInvoices: number;
  pushedBills: number;
  pushedOther: number;
  waiting: number;
  blocked: number;
  inReview: number;
  failed: number;
}

export async function loadDashboard(source: DataSource): Promise<DashboardCounts> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    return fixtures.DASHBOARD;
  }
  return getJson<DashboardCounts>('/api/dashboard');
}

export async function loadPeople(source: DataSource): Promise<PeopleResponse> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    return fixtures.PEOPLE as unknown as PeopleResponse;
  }
  return getJson<PeopleResponse>("/api/people");
}

/** The reason an invitation was refused, in words a person can act on. */
const REVOKE_ERRORS: Record<string, string> = {
  cannot_remove_firm_member:
    'Only CFO Advisory and Accountancy can remove one of its own people.',
};

const INVITE_ERRORS: Record<string, string> = {
  invalid_email: "That does not look like an email address.",
  already_has_access: "That person already has access to this organisation.",
  already_invited: "That address has already been invited.",
  role_not_grantable: "You cannot grant that role.",
};

/**
 * What happened to the password that was chosen, if one was.
 *
 * `false` where a password was sent and the directory already had an account
 * for that address — the creation is refused whole, password included, and
 * the screen has to stop offering it rather than let somebody read out a
 * password that will not work.
 */
export interface InviteResult {
  passwordApplied: boolean;
}

export async function invitePerson(
  email: string,
  role: InvitableRole,
  name: string | null,
  password: string | null,
  source: DataSource,
): Promise<InviteResult> {
  /*
    On invented data there is no directory to call, and the thing worth
    looking at on this screen is what happens after a password is set.
    Without this the demo stops at a 404 from the dev server and the panel
    that shows the password — the part most likely to be got wrong — could
    never be seen before it was deployed.
  */
  if (import.meta.env.DEV && source === 'local-fixtures') {
    return { passwordApplied: password !== null };
  }

  const response = await fetch("/api/people/invite", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, role, name, password }),
  });

  if (response.ok) {
    const ok = (await response.json().catch(() => null)) as { passwordApplied?: boolean } | null;
    return { passwordApplied: Boolean(ok?.passwordApplied) };
  }

  const body = (await response.json().catch(() => null)) as
    | { error?: string; message?: string }
    | null;
  // The directory's own complaint about a password beats our generic line:
  // it is the only one that says which rule was broken.
  if (body?.error === 'weak_password' && body.message) throw new Error(body.message);
  throw new Error(INVITE_ERRORS[body?.error ?? ""] ?? "That did not work. Please try again.");
}

const PASSWORD_ERRORS: Record<string, string> = {
  cannot_set_firm_password:
    'Only CFO Advisory and Accountancy can set the password of one of its own people.',
  account_not_ours:
    'Their sign-in was not created here, so its password cannot be set from this screen. They can still use "Forgot password?" on the sign-in page.',
  not_permitted:
    'This service has not been given permission to change passwords in the directory. An administrator has to grant it once: the User-PasswordProfile.ReadWrite.All permission, and the User Administrator role on the app itself. Both are needed.',
  unavailable: 'The directory did not answer. Nothing was changed. Please try again.',
  refused: 'The directory refused that password.',
};

/**
 * Give somebody a new password, and be the one who knows what it is.
 *
 * Nothing is stored. What comes back is only whether it worked — the caller
 * already holds the password, because the caller is who chose it.
 */
export async function setPersonPassword(
  target: { userId?: number; invitationId?: number },
  password: string,
  source: DataSource,
): Promise<void> {
  if (import.meta.env.DEV && source === 'local-fixtures') return;

  const response = await fetch("/api/people/password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...target, password }),
  });
  if (response.ok) return;

  const body = (await response.json().catch(() => null)) as
    | { error?: string; message?: string }
    | null;
  if (body?.error === 'weak_password' && body.message) throw new Error(body.message);
  throw new Error(
    PASSWORD_ERRORS[body?.error ?? ""] ?? "That did not work. Please try again.",
  );
}

/**
 * Try again to create a sign-in that did not get made.
 *
 * Safe to press more than once: an address the directory already knows comes
 * back as already existing, which counts as success.
 */
export async function createSignIn(invitationId: number): Promise<void> {
  const response = await fetch("/api/people/create-sign-in", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ invitationId }),
  });
  if (!response.ok) throw new Error("The sign-in could not be created. Please try again.");
}

export async function revokePerson(target: { userId?: number; invitationId?: number }): Promise<void> {
  const response = await fetch("/api/people/revoke", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(target),
  });
  if (response.ok) return;

  const body = (await response.json().catch(() => null)) as { error?: string } | null;
  throw new Error(REVOKE_ERRORS[body?.error ?? ""] ?? "That did not work. Please try again.");
}


/* ---------------------------------------------------------------------------
   What a company sold and bought.
--------------------------------------------------------------------------- */

export interface SummaryResponse {
  from: string;
  to: string;
  organisation: string;
  currency: string;
  sales: { count: number; total: number };
  purchases: { count: number; total: number };
  months: { month: string; sales: number; purchases: number }[];
  topItems: { code: string; name: string; quantity: number; amount: number }[];
  excluded: { drafts: number; voided: number };
  documentsRead: number;
  truncated: boolean;
}

export async function loadSummary(from: string, to: string): Promise<SummaryResponse> {
  return getJson<SummaryResponse>(
    `/api/summary?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
  );
}


/* ---------------------------------------------------------------------------
   Payments.
--------------------------------------------------------------------------- */

export interface PayableInvoice {
  id: string;
  number: string;
  reference: string;
  contact: string;
  due: string | null;
  amountDue: number;
  total: number;
  direction: 'in' | 'out';
  currency: string;
}

/** A way of being paid, named in both languages. */
export interface PaymentMethodOption {
  code: string;
  en: string;
  ar: string;
}

export interface PaymentOptions {
  invoices: PayableInvoice[];
  /** Where a payment may go. Xero's bank accounts, plus any account
   *  explicitly set to receive payments. 'type' is Xero's own. */
  accounts: { code: string; name: string; type?: string }[];
  currency: string;
  /** The ways this company is paid, in a fixed order. */
  methods: PaymentMethodOption[];
  /** The one the form starts on. */
  defaultMethod: string | null;
}

export async function loadPaymentOptions(source: DataSource): Promise<PaymentOptions> {
  if (import.meta.env.DEV && source === 'local-fixtures') return fixtures.PAYMENT_OPTIONS;
  return getJson<PaymentOptions>('/api/payments/options');
}

export async function recordPayment(payment: {
  invoiceId: string;
  accountCode: string;
  method: string;
  date: string;
  amount: string;
  reference: string;
}): Promise<void> {
  const response = await fetch('/api/payments', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payment),
  });
  if (response.ok) return;

  const text = await response.text();
  try {
    const parsed = JSON.parse(text) as { error?: string; messages?: string[] };
    if (parsed.error === 'xero_rejected' && parsed.messages?.length) {
      throw new XeroRejectedError(parsed.messages);
    }
    throw new Error(parsed.error ?? 'That did not work. Please try again.');
  } catch (error) {
    if (error instanceof XeroRejectedError) throw error;
    throw new Error('That did not work. Please try again.');
  }
}





/* ---------------------------------------------------------------------------
   Training: the catalogue, the runs, and who is on them.

   None of this touches Xero. It is what a training centre knows and a ledger
   has nowhere to put. How a delivery is linked to a Xero transaction — and
   therefore how profit per course is read back — is still an open question;
   everything here is true either way.
--------------------------------------------------------------------------- */

export type DeliveryKind = 'public' | 'corporate';
export type DeliveryStatus = 'planned' | 'running' | 'finished' | 'cancelled';
export type DeliveryPhase = 'cancelled' | 'finished' | 'running' | 'upcoming';
export type RegistrationStatus = 'registered' | 'attended' | 'cancelled' | 'refunded';

export interface TrainingCourse {
  courseId: number;
  name: string;
  nameAr: string | null;
  code: string | null;
  defaultFee: number | null;
  active: boolean;
  /** The Xero product this is sold as. Null when it is not sold as one. */
  xeroItemCode: string | null;
  deliveries: number;
}

/**
 * Sell a course as a product in Xero, or stop.
 *
 * Optional: Xero needs only a description on an invoice line, and the
 * invoices this product raises are valid without it. What it buys is that
 * the breakdown by course exists inside Xero rather than only here.
 *
 * `existingCode` links to a product that already exists instead of making
 * one. `unlink` drops our link and leaves the Xero product alone.
 */
export async function setCourseItem(
  courseId: number,
  body: { existingCode?: string | null; unlink?: boolean },
  source: DataSource,
): Promise<void> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    fixtures.setCourseItem(courseId, body);
    return;
  }

  const response = await fetch(`/api/training/courses/${courseId}/item`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (response.ok) return;

  const text = await response.text();
  let parsed: { error?: string } | null = null;
  try {
    parsed = JSON.parse(text) as { error?: string };
  } catch {
    throw new Error('That did not work. Please try again.');
  }

  switch (parsed?.error) {
    case 'needs_a_code':
      throw new Error('Give this course a code first — Xero needs one for a product.');
    case 'not_permitted':
      throw new Error('Only an administrator can add a product to Xero.');
    default:
      throw new Error('That did not work. Please try again.');
  }
}

export interface TrainingDelivery {
  deliveryId: number;
  courseId: number;
  courseName: string;
  defaultFee: number | null;
  name: string;
  code: string | null;
  kind: DeliveryKind;
  startsOn: string;
  endsOn: string | null;
  trainer: string | null;
  venue: string | null;
  capacity: number | null;
  companyContactId: string | null;
  contractFee: number | null;
  status: DeliveryStatus;
  notes: string | null;
  liveRegistrations: number;
  /** The single invoice for a corporate engagement. Null for a public one. */
  xeroInvoiceId: string | null;
  /** Where this course's revenue lands. Null means the sales default. */
  revenueAccountCode: string | null;
  /** When the books were closed on this cohort. Null while it is open. */
  closedAt: string | null;
  /** Read from the calendar, not stored. See api/src/domain/training.ts. */
  phase: DeliveryPhase;
}

export interface TrainingRegistration {
  registrationId: number;
  studentName: string;
  studentEmail: string | null;
  studentPhone: string | null;
  billContactId: string | null;
  fee: number | null;
  discount: number;
  status: RegistrationStatus;
  xeroInvoiceId: string | null;
  registeredOn: string;
}

export interface CohortNumbers {
  live: number;
  cancelled: number;
  total: number;
  seatsLeft: number | null;
  percentFull: number | null;
  oversold: boolean;
  expectedRevenue: number;
  invoicedSeats: number;
}

export interface TrainingOverview {
  currency: string;
  courses: TrainingCourse[];
  deliveries: TrainingDelivery[];
}

/** What became of an invoice, in the same words the invoice list uses. */
export interface InvoiceState {
  invoiceId: string;
  number: string;
  status: string;
  /** Excluding tax. What was earned, as opposed to what was asked for. */
  subTotal: number;
  total: number;
  amountDue: number;
  amountPaid: number;
  /**
   * Handed back on a credit note, including tax. Xero leaves SubTotal at
   * the invoice's original value when one is allocated, so without this an
   * invoice looks like it earned money the student got back.
   */
  amountCredited: number;
  /** The invoice's own currency, which need not be the company's. */
  currencyCode: string;
  due: string | null;
}

/** A bill or spend-money put on a course, with Xero's own figures. */
export interface CostDetail {
  costId: number;
  source: 'bill' | 'spend';
  xeroId: string;
  /** The share on this course, or null for the whole document. */
  allocatedAmount: number | null;
  note: string | null;
  linkedAt: string;
  description: string;
  supplier: string;
  date: string | null;
  status: string;
  subTotal: number;
  total: number;
  /** The document's own currency, which need not be the company's. */
  currencyCode: string;
  /** Xero no longer has it, or could not be read. Not counted. */
  missing: boolean;
  /** How many other courses also carry this document. */
  onOtherCourses: number;
  /** What every course together claims of it, excluding tax. */
  claimedTotal: number;
  /** More has been put on courses than the document is worth. */
  overAllocated: boolean;
}

/**
 * Money given back on a course.
 *
 * The refunded-after-paying case: Xero only allocates a credit note
 * against a balance still owing, so a student refunded after paying in
 * full leaves an invoice reading PAID and a credit note standing alone.
 */
export interface CreditDetail {
  creditId: number;
  xeroId: string;
  /** The share on this course, or null for the whole credit note. */
  allocatedAmount: number | null;
  note: string | null;
  linkedAt: string;
  number: string;
  contact: string;
  date: string | null;
  status: string;
  subTotal: number;
  total: number;
  currencyCode: string;
  /** Already allocated to this cohort's invoices, so counted there. */
  allocatedToCohort: number;
  missing: boolean;
}

/** A credit note that could be put on a course. */
export interface CreditOption {
  xeroId: string;
  number: string;
  contact: string;
  date: string | null;
  status: string;
  subTotal: number;
  total: number;
  currencyCode: string;
  usedElsewhere: boolean;
}

export async function loadCreditOptions(
  source: DataSource,
): Promise<{ credits: CreditOption[]; currency: string }> {
  if (import.meta.env.DEV && source === 'local-fixtures') return fixtures.creditOptions();
  return getJson<{ credits: CreditOption[]; currency: string }>('/api/training/credits/available');
}

export async function linkCredit(
  deliveryId: number,
  credit: { xeroId: string; allocatedAmount: number | null; note: string | null },
  source: DataSource,
): Promise<void> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    fixtures.linkCredit(deliveryId, credit);
    return;
  }
  await trainingWrite(`deliveries/${deliveryId}/credits`, 'POST', credit);
}

export async function unlinkCredit(
  deliveryId: number,
  creditId: number,
  source: DataSource,
): Promise<void> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    fixtures.unlinkCredit(deliveryId, creditId);
    return;
  }
  await trainingWrite(`deliveries/${deliveryId}/credits/${creditId}`, 'DELETE', null);
}

/** A cost that could be put on a course. */
export interface CostOption {
  source: 'bill' | 'spend';
  xeroId: string;
  description: string;
  supplier: string;
  date: string | null;
  status: string;
  subTotal: number;
  total: number;
  /** The document's own currency, which need not be the company's. */
  currencyCode: string;
  /** Already on another course. Shown, because one bill can cover three. */
  usedElsewhere: boolean;
}

/** Documents left out of the figures because they are in another currency. */
export interface ForeignDocuments {
  currency: string;
  documents: number;
}

/** What the course made and what it cost. See api/src/domain/deliveryResult.ts. */
export interface DeliveryResult {
  /** The currency these figures are in, which may not be the company's. */
  currency: string;
  /** Left out because the cohort mixes currencies. Empty in the usual case. */
  otherCurrencies: ForeignDocuments[];
  madeNet: number;
  spentNet: number;
  result: number;
  marginPercent: number | null;
  collected: number;
  /** Given back on credit notes, including tax. */
  credited: number;
  outstanding: number;
  invoiceCount: number;
  costCount: number;
  draftInvoices: number;
  /** Invoices carrying a credit note, so a reduced figure is explainable. */
  creditedInvoices: number;
}

export interface DeliveryDetail {
  currency: string;
  delivery: TrainingDelivery;
  registrations: TrainingRegistration[];
  numbers: CohortNumbers;
  /** Keyed by Xero InvoiceID. Empty when Xero could not be reached. */
  invoices: Record<string, InvoiceState>;
  costs: CostDetail[];
  /** Money given back that no invoice on this cohort knows about. */
  credits: CreditDetail[];
  result: DeliveryResult;
  /** The result divided by live seats. Null for a corporate engagement. */
  perSeat: number | null;
  /** Why the cohort cannot be billed, if it cannot. */
  cannotInvoice: string | null;
  /**
   * What the screen should offer as the invoice date: the day the course
   * finished, or today where it has not. The person can change it.
   */
  suggestedInvoiceDate: string;
}

export async function loadCostOptions(
  source: DataSource,
): Promise<{ costs: CostOption[]; currency: string }> {
  if (import.meta.env.DEV && source === 'local-fixtures') return fixtures.costOptions();
  return getJson<{ costs: CostOption[]; currency: string }>('/api/training/costs/available');
}

export async function linkCost(
  deliveryId: number,
  cost: { source: 'bill' | 'spend'; xeroId: string; allocatedAmount: number | null; note: string | null },
  source: DataSource,
): Promise<void> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    fixtures.linkCost(deliveryId, cost);
    return;
  }
  await trainingWrite(`deliveries/${deliveryId}/costs`, 'POST', cost);
}

export async function unlinkCost(
  deliveryId: number,
  costId: number,
  source: DataSource,
): Promise<void> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    fixtures.unlinkCost(deliveryId, costId);
    return;
  }
  await trainingWrite(`deliveries/${deliveryId}/costs/${costId}`, 'DELETE', null);
}

/* ---------------------------------------------------------------------------
   Training setup: which costs belong to a course, and what has fallen out.
--------------------------------------------------------------------------- */

/** How the course reaches Xero, read from Xero. */
export interface TrackingState {
  categories: { id: string; name: string; active: boolean; activeOptions: number; ours: boolean }[];
  activeCategories: number;
  /** Xero allows two active categories per organisation. */
  slotsFree: number;
  categoryId: string | null;
  categoryName: string | null;
  /** Room left for new courses. Null until a category exists. */
  budget: { active: number; remaining: number; full: boolean; nearlyFull: boolean } | null;
}

export interface TrainingSetup {
  /** Xero account codes a training cost is posted to. */
  directCostAccounts: string[];
  /** Null when Xero could not be read — not the same as "none". */
  tracking: TrackingState | null;
}

export interface UnaccountedBills {
  /** False until somebody has said which accounts count. */
  configured: boolean;
  bills: {
    xeroId: string;
    description: string;
    supplier: string;
    date: string | null;
    subTotal: number;
    currencyCode: string;
    accountCodes: string[];
  }[];
  /** Xero could not be read, so nothing was checked. */
  unavailable?: boolean;
  /** Only the most recent page was scanned. Said, never assumed. */
  truncated?: boolean;
}

export async function loadTrainingSetup(source: DataSource): Promise<TrainingSetup> {
  if (import.meta.env.DEV && source === 'local-fixtures') return fixtures.trainingSetup();
  return getJson<TrainingSetup>('/api/training/setup');
}

export async function loadUnaccountedBills(source: DataSource): Promise<UnaccountedBills> {
  if (import.meta.env.DEV && source === 'local-fixtures') return fixtures.unaccountedBills();
  return getJson<UnaccountedBills>('/api/training/unaccounted');
}

export async function saveDirectCostAccounts(
  accounts: string[],
  source: DataSource,
): Promise<void> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    fixtures.saveDirectCostAccounts(accounts);
    return;
  }
  await trainingWrite('setup/accounts', 'POST', { accounts });
}

/**
 * Take a finished course out of Xero's active tracking list.
 *
 * Xero allows 100 active options per category. Archiving keeps the list
 * short while leaving every document already stamped reporting under it.
 */
export async function archiveTrackingOption(
  deliveryId: number,
  source: DataSource,
): Promise<void> {
  if (import.meta.env.DEV && source === 'local-fixtures') return;
  await trainingWrite(`deliveries/${deliveryId}/archive-option`, 'POST', {});
}

export async function setDeliveryClosed(
  deliveryId: number,
  closed: boolean,
  source: DataSource,
): Promise<void> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    fixtures.setDeliveryClosed(deliveryId, closed);
    return;
  }
  await trainingWrite(`deliveries/${deliveryId}/close`, 'POST', { closed });
}

/** One place to turn a training refusal into a sentence somebody can act on. */
async function trainingWrite(path: string, method: string, body: unknown): Promise<void> {
  const response = await fetch(`/api/training/${path}`, {
    method,
    ...(body === null
      ? {}
      : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  });
  if (response.ok) return;

  const text = await response.text();
  let parsed: { error?: string } | null = null;
  try {
    parsed = JSON.parse(text) as { error?: string };
  } catch {
    throw new Error('That did not work. Please try again.');
  }

  switch (parsed?.error) {
    case 'delivery_is_closed':
      throw new Error('This course is closed. Reopen it to make changes.');
    case 'not_permitted':
      throw new Error('Only an administrator can close or reopen a course.');
    case 'not_found':
      throw new Error('That has been removed by somebody else.');
    default:
      throw new Error('That did not work. Please try again.');
  }
}

export interface InvoicedResult {
  created: { registrationId: number | null; invoiceId: string; total: number }[];
  failed: {
    registrationId: number | null;
    name: string;
    reason: string;
    /** What Xero itself said, where it said anything useful. */
    detail: string | null;
  }[];
  status: 'DRAFT' | 'AUTHORISED';
  /** The date the invoices were given. */
  date: string;
}

/**
 * Bill a cohort.
 *
 * `registrationIds` null means every live booking that has not been billed
 * yet. A corporate delivery ignores it and raises one invoice to the
 * company, because that is what was sold.
 */
export async function invoiceCohort(
  deliveryId: number,
  registrationIds: number[] | null,
  /** The date the invoices carry. Null lets the server decide. */
  date: string | null,
  /**
   * When they fall due.
   *
   * Null sends no DueDate at all, so the client's own Xero default
   * applies — Al Maer set 7 days in their Invoice settings. A date here
   * overrides it for this run only.
   */
  dueDate: string | null,
  source: DataSource,
): Promise<InvoicedResult> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    return fixtures.invoiceCohort(deliveryId, registrationIds, 'AUTHORISED', date);
  }

  const response = await fetch(`/api/training/deliveries/${deliveryId}/invoice`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ registrationIds, date, dueDate }),
  });

  if (response.ok) return (await response.json()) as InvoicedResult;

  const text = await response.text();
  let parsed: { error?: string } | null = null;
  try {
    parsed = JSON.parse(text) as { error?: string };
  } catch {
    throw new Error('That did not work. Please try again.');
  }

  switch (parsed?.error) {
    case 'nobody_to_invoice':
      // Nothing happened, so no date was used. The caller only reads this
      // to refresh; the field is here because the shape requires it.
      return { created: [], failed: [], status: 'AUTHORISED' as const, date: date ?? '' };
    case 'already_invoiced':
      throw new Error('Everything on this course has already been invoiced.');
    case 'no_fees_to_bill':
      throw new Error('Nobody on this course has a fee to bill.');
    case 'corporate_needs_a_company':
      throw new Error('Choose the company this corporate booking is for.');
    case 'corporate_needs_a_fee':
      throw new Error('Enter the agreed contract fee for this corporate booking.');
    case 'nothing_to_invoice':
      throw new Error('There is nothing left to invoice here.');
    default:
      throw new Error('That did not work. Please try again.');
  }
}

export async function loadTraining(source: DataSource): Promise<TrainingOverview> {
  if (import.meta.env.DEV && source === 'local-fixtures') return fixtures.TRAINING;
  return getJson<TrainingOverview>('/api/training');
}

export async function loadDelivery(
  deliveryId: number,
  source: DataSource,
): Promise<DeliveryDetail> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    return fixtures.deliveryDetail(deliveryId);
  }
  return getJson<DeliveryDetail>(`/api/training/deliveries/${deliveryId}`);
}

export interface CourseInput {
  courseId: number | null;
  name: string;
  nameAr: string | null;
  code: string | null;
  defaultFee: number | null;
  active: boolean;
}

export interface DeliveryInput {
  deliveryId: number | null;
  courseId: number;
  name: string;
  code: string | null;
  kind: DeliveryKind;
  startsOn: string;
  endsOn: string | null;
  trainer: string | null;
  venue: string | null;
  capacity: number | null;
  companyContactId: string | null;
  contractFee: number | null;
  status: DeliveryStatus;
  notes: string | null;
}

export interface RegistrationInput {
  registrationId: number | null;
  deliveryId: number;
  studentName: string;
  studentEmail: string | null;
  studentPhone: string | null;
  billContactId: string | null;
  fee: number | null;
  discount: number;
  status: RegistrationStatus;
}

/** The saved record, where the caller needs what came back. */
interface TrainingSaveResult {
  registrationId?: number;
  contactId?: string | null;
  /** The booking saved, but Xero would not take the contact. */
  contactFailed?: boolean;
}

async function postTraining(
  path: string,
  body: unknown,
  source: DataSource,
): Promise<TrainingSaveResult | null> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    fixtures.saveTraining(path, body);
    return null;
  }

  const response = await fetch(`/api/training/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (response.ok) {
    return (await response.json().catch(() => null)) as TrainingSaveResult | null;
  }

  const text = await response.text();
  let parsed: { error?: string } | null = null;
  try {
    parsed = JSON.parse(text) as { error?: string };
  } catch {
    throw new Error('That did not work. Please try again.');
  }

  switch (parsed?.error) {
    case 'ends_before_it_starts':
      throw new Error('The end date is before the start date.');
    case 'course_not_found':
      throw new Error('That course no longer exists.');
    case 'delivery_not_found':
    case 'not_found':
      throw new Error('That has been removed by somebody else.');
    default:
      throw new Error('That did not work. Please try again.');
  }
}

/**
 * An optional box the user left alone is absent, not empty.
 *
 * A form binds a text input to '' because React controls it, but '' in the
 * database is a different claim from NULL: it says somebody entered nothing,
 * where NULL says nobody was asked. It also defeats every `?? fallback` on
 * the way back out, which is how a student with no email ended up with a
 * blank cell instead of a dash.
 */
function blanksToNull<T extends object>(input: T): T {
  const out: Record<string, unknown> = { ...(input as object) } as Record<string, unknown>;
  for (const [key, value] of Object.entries(out)) {
    if (typeof value === 'string' && value.trim() === '') out[key] = null;
  }
  return out as T;
}

/** Neither of these needs what comes back; only the registration does. */
export async function saveCourse(input: CourseInput, source: DataSource): Promise<void> {
  await postTraining('courses', blanksToNull(input), source);
}

export async function saveDelivery(input: DeliveryInput, source: DataSource): Promise<void> {
  await postTraining('deliveries', blanksToNull(input), source);
}

/**
 * Enrol a student.
 *
 * Nothing reaches Xero here. A student becomes a Xero customer when they
 * are invoiced, not when they are pencilled in — see
 * api/src/functions/trainingInvoicing.ts.
 */
export async function saveRegistration(
  input: RegistrationInput,
  source: DataSource,
): Promise<void> {
  await postTraining('registrations', blanksToNull(input), source);
}
/* ---------------------------------------------------------------------------
   Reports.

   Only Profit and Loss is a Xero report endpoint. Xero publishes no sales tax
   report, and its aged reports take a required contactId — so the other three
   are computed from the documents. Each response says which it is, in
   `source`, and the screen passes that on to the reader.
--------------------------------------------------------------------------- */

export interface FlatReportRow {
  kind: 'header' | 'row' | 'total';
  label: string;
  values: string[];
  accountId: string | null;
}

export interface FlatReportSection {
  title: string;
  rows: FlatReportRow[];
}

/**
 * Any report Xero publishes, in the one shape they all arrive in.
 *
 * Profit and loss, balance sheet and the aged detail differ in what they
 * mean and not at all in their structure — Xero returns titles, columns and
 * sections of rows for every one of them. So there is one type and one
 * table, and what distinguishes them is which endpoint was called and what
 * the screen says above it.
 */
export interface XeroReportResponse {
  source: 'xero';
  currency: string;
  /** How many comparison periods Xero returned. 0 for a single column. */
  periods: number;
  timeframe: string | null;
  /** The day a balance sheet or aged detail was struck. Absent on a P&L. */
  date?: string;
  report: {
    titles: string[];
    reportDate: string | null;
    columns: string[];
    sections: FlatReportSection[];
  };
}

/** Kept as a name because the profit and loss is what most callers mean. */
export type ProfitAndLossResponse = XeroReportResponse;

export interface AgedBuckets {
  current: number;
  days1to30: number;
  days31to60: number;
  days61to90: number;
  days90plus: number;
  total: number;
}

export interface AgedRow extends AgedBuckets {
  contactId: string;
  contact: string;
  documents: number;
}

export interface AgedResponse {
  source: 'computed';
  asAt: string;
  currency: string;
  rows: AgedRow[];
  totals: AgedBuckets;
  /** Invoices with no due date, counted as current. Shown, not hidden. */
  undated: number;
  documentsRead: number;
  truncated: boolean;
}

export interface TaxRateTotals {
  taxType: string;
  name: string;
  netSales: number;
  taxOnSales: number;
  netPurchases: number;
  taxOnPurchases: number;
}

export interface SalesTaxResponse {
  source: 'computed';
  from: string;
  to: string;
  currency: string;
  rows: TaxRateTotals[];
  totals: Omit<TaxRateTotals, 'taxType' | 'name'>;
  net: number;
  documentsRead: number;
  excluded: { drafts: number; voided: number };
  truncated: boolean;
  basis: 'accrual';
  excludes: string[];
}

export async function loadProfitAndLoss(
  from: string,
  to: string,
  /** Comparison periods. 0 shows the chosen range as a single column. */
  periods: number,
  source: DataSource,
): Promise<ProfitAndLossResponse> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    return fixtures.profitAndLoss(periods);
  }
  return getJson<ProfitAndLossResponse>(
    `/api/reports/profit-and-loss?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}` +
      (periods > 0 ? `&periods=${periods}&timeframe=MONTH` : ''),
  );
}

export async function loadBalanceSheet(
  /** The day the balance is struck. A moment, not a range. */
  date: string,
  periods: number,
  source: DataSource,
): Promise<XeroReportResponse> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    return fixtures.balanceSheet(date);
  }
  return getJson<XeroReportResponse>(
    `/api/reports/balance-sheet?date=${encodeURIComponent(date)}` +
      (periods > 0 ? `&periods=${periods}&timeframe=MONTH` : ''),
  );
}

/**
 * Everything one contact owes, or is owed, document by document.
 *
 * One contact per call, because that is the only shape Xero's endpoint has.
 * Reached by clicking a name on the aged summary, which already holds the
 * contact id — so this costs one call for the one name somebody asked about,
 * rather than one per contact for a list nobody read.
 */
export async function loadAgedDetail(
  direction: 'receivable' | 'payable',
  contactId: string,
  date: string,
  source: DataSource,
): Promise<XeroReportResponse> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    return fixtures.agedDetail(direction, contactId, date);
  }
  const path = direction === 'receivable' ? 'aged-receivables-detail' : 'aged-payables-detail';
  return getJson<XeroReportResponse>(
    `/api/reports/${path}?contactId=${encodeURIComponent(contactId)}&date=${encodeURIComponent(date)}`,
  );
}

export async function loadAged(
  direction: 'receivable' | 'payable',
  source: DataSource,
): Promise<AgedResponse> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    return direction === 'receivable' ? fixtures.AGED_RECEIVABLES : fixtures.AGED_PAYABLES;
  }
  const path = direction === 'receivable' ? 'aged-receivables' : 'aged-payables';
  return getJson<AgedResponse>(`/api/reports/${path}`);
}

export async function loadSalesTax(
  from: string,
  to: string,
  source: DataSource,
): Promise<SalesTaxResponse> {
  if (import.meta.env.DEV && source === 'local-fixtures') return fixtures.SALES_TAX;
  return getJson<SalesTaxResponse>(
    `/api/reports/sales-tax?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
  );
}
/* ---------------------------------------------------------------------------
   Money that no invoice explains.

   Spend money, receive money, transfer money — Xero's own three, and the
   whole of a small company's bank statement that the document forms never
   covered. See api/src/domain/money.ts.
--------------------------------------------------------------------------- */

export interface MoneyLineEntry {
  description: string;
  accountCode: string;
  taxType: string;
  amount: string;
}

export type MoneyEntry =
  | {
      kind: 'spend' | 'receive';
      contactId: string;
      bankAccountCode: string;
      date: string;
      reference: string;
      amountsAre: LineAmountType;
      lines: MoneyLineEntry[];
      /** Put this on a training course as it is filed. Spend only. */
      deliveryId: number | null;
    }
  | {
      kind: 'transfer';
      fromAccountCode: string;
      toAccountCode: string;
      date: string;
      amount: string;
      reference: string;
    };

/** What the server said, so the screen can show the same words to everyone. */
export interface MoneyResult {
  endpoint: string;
  message?: string;
}

/** A refusal: either Xero's complaints, or one of our own codes. */
interface MoneyRefusalBody {
  error?: string;
  messages?: string[];
}

export async function recordMoney(entry: MoneyEntry, source: DataSource): Promise<MoneyResult> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    // The course link is applied in the demo too, so the dropdown on the
    // money-out form visibly does what it says.
    if (entry.kind === 'spend' && entry.deliveryId !== null) {
      fixtures.linkSpendToDelivery(
        entry.deliveryId,
        `demo-spend-${Date.now()}`,
        entry.lines[0]?.description || 'Money out',
      );
    }
    return {
      endpoint: entry.kind === 'transfer' ? 'PUT /BankTransfers' : 'PUT /BankTransactions',
      message: 'Demonstration only — nothing was sent to Xero.',
    };
  }

  const response = await fetch('/api/money', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(entry),
  });

  if (response.ok) return (await response.json()) as MoneyResult;

  const text = await response.text();
  let parsed: MoneyRefusalBody | null = null;
  try {
    parsed = JSON.parse(text) as MoneyRefusalBody;
  } catch {
    throw new Error('That did not work. Please try again.');
  }

  if (parsed?.error === 'xero_rejected' && parsed.messages?.length) {
    throw new XeroRejectedError(parsed.messages);
  }

  /*
    Our own refusals, not Xero's.

    Each is a sentence the screen has an Arabic translation for, rather than a
    code shown to the person who typed the entry. They are mapped here because
    this is the only place that knows both what the server answered and what
    the screen can say.
  */
  throw new Error(moneyRefusal(parsed?.error));
}

function moneyRefusal(code: string | undefined): string {
  switch (code) {
    case 'amount_must_be_positive':
      return 'Enter an amount greater than zero.';
    case 'transfer_needs_two_accounts':
      return 'A transfer needs two different accounts.';
    case 'nothing_to_record':
      return 'Nothing has been entered yet.';
    case 'line_needs_a_category':
      // The line number is left out on purpose. A sentence with a number in
      // it cannot be looked up in the Arabic dictionary, and the form marks
      // the offending row in red as it is typed, which says more than the
      // number would. See i18n/messages.ts for the case where it is worth it.
      return 'Every line with an amount needs a category.';
    case 'invalid_date':
      return 'That date could not be read.';
    case 'not_permitted':
      return 'You are not allowed to record this for this company.';
    default:
      return 'That did not work. Please try again.';
  }
}
/* ---------------------------------------------------------------------------
   One company Xero connection, as it actually is.
--------------------------------------------------------------------------- */

export interface ConnectionDetail {
  organisation: string;
  status: string;
  /** Null means we did not record it, which is not the same as none. */
  granted: string[] | null;
  requested: string[];
  missing: string[];
  needsReconnect: boolean;
  /** The two switches the practice holds for this client. */
  features: { training: boolean; xeroProjects: boolean; amountsAre: LineAmountType };
  accessToken: { expiresAt: string | null; expiresInMinutes: number | null; valid: boolean };
  refreshToken: {
    rotatedAt: string | null;
    ageDays: number | null;
    windowDays: number;
    daysRemaining: number | null;
  };
  lastRefreshAt: string | null;
  lastRefreshError: string | null;
  consecutiveFailures: number;
}

export async function loadConnectionDetail(): Promise<ConnectionDetail> {
  try {
    return await getJson<ConnectionDetail>('/api/connection');
  } catch (error) {
    // Local development has no Xero app registered and no connection to
    // report on, so the screen would be permanently empty — including the
    // two switches, which are the part most worth being able to drive.
    if (import.meta.env.DEV) return fixtures.connectionDetail();
    throw error;
  }
}

/**
 * Turn one of the two switches on or off for the company now open.
 *
 * Either may be omitted: the screen has two switches and flipping one
 * should not restate the other.
 *
 * Turning Projects on does not connect anything — a Xero scope is granted
 * by the client at Xero's own consent screen and nothing here can grant
 * one for them. So the answer comes back saying the connection is now
 * short of that permission, and the screen asks for a reconnect.
 */
export async function setOrganisationFeatures(
  features: { training?: boolean; xeroProjects?: boolean; amountsAre?: LineAmountType },
  source: DataSource,
): Promise<{
  features: { training: boolean; xeroProjects: boolean; amountsAre: LineAmountType };
  missing: string[];
  needsReconnect: boolean;
}> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    return fixtures.setOrganisationFeatures(features);
  }

  const response = await fetch('/api/organisation/features', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(features),
  });

  if (response.ok) {
    return (await response.json()) as {
      features: { training: boolean; xeroProjects: boolean; amountsAre: LineAmountType };
      missing: string[];
      needsReconnect: boolean;
    };
  }
  if (response.status === 403) {
    throw new Error('Only CFOAI can change what a client is given.');
  }
  throw new Error('That did not work. Please try again.');
}

/**
 * Where to send somebody to consent, and what to ask for while they are there.
 *
 * The two switches ride on the link because the company row does not exist
 * until Xero hands the tenant back — so for a new client this is the only
 * place the choice can be made. `orgId` pins an existing company, so
 * reconnecting one client cannot change what the others were given.
 */
export function connectUrl(options: {
  orgId?: number;
  training?: boolean;
  projects?: boolean;
} = {}): string {
  const query = new URLSearchParams();
  if (options.orgId !== undefined) query.set('orgId', String(options.orgId));
  if (options.training !== undefined) query.set('training', options.training ? '1' : '0');
  if (options.projects !== undefined) query.set('projects', options.projects ? '1' : '0');
  const suffix = query.toString();
  return suffix === '' ? '/api/auth/connect' : `/api/auth/connect?${suffix}`;
}


/* ---------------------------------------------------------------------------
   Branding: the logo on documents this app prints itself.
--------------------------------------------------------------------------- */

export interface BrandingFooter {
  email: string;
  phone: string;
  address: string;
}

export interface Branding {
  organisation: string;
  hasLogo: boolean;
  /** Where to draw the logo from; unique per company and per upload. */
  logoUrl: string | null;
  contentType: string | null;
  fileName: string | null;
  uploadedAt: string | null;
  bytes: number;
  footer: BrandingFooter;
  presentation: Presentation;
}

/** How this company's printed documents look, and whose details are on them. */
export async function saveBranding(
  footer: BrandingFooter,
  presentation: Presentation,
  source: DataSource,
): Promise<void> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    // Kept in memory only, so the screen behaves while there is no server.
    fixtures.BRANDING.footer = footer;
    fixtures.BRANDING.presentation = presentation;
    return;
  }
  const response = await fetch('/api/branding', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...footer, presentation }),
  });
  if (response.ok) return;
  const body = (await response.json().catch(() => null)) as { error?: string; field?: string } | null;
  if (body?.error === 'not_a_colour') throw new Error('A colour has to be written as #rrggbb, for example #9b2242.');
  throw new Error('That did not work. Please try again.');
}

export async function loadBranding(source: DataSource): Promise<Branding> {
  if (import.meta.env.DEV && source === "local-fixtures") return fixtures.BRANDING;
  return getJson<Branding>("/api/branding");
}

const LOGO_ERRORS: Record<string, string> = {
  unsupported_type: "That has to be a PNG, JPEG or WebP image.",
  not_the_image_it_claims_to_be: "That file is not the kind of image it says it is.",
  too_large: "That image is too large. The limit is 256 KB.",
  empty_file: "That file is empty.",
};

export async function uploadLogo(file: File): Promise<void> {
  const response = await fetch("/api/branding/logo", {
    method: "PUT",
    headers: { "Content-Type": file.type, "X-File-Name": encodeURIComponent(file.name).slice(0, 255) },
    body: file,
  });
  if (response.ok) return;
  const body = (await response.json().catch(() => null)) as { error?: string } | null;
  throw new Error(LOGO_ERRORS[body?.error ?? ""] ?? "That did not work. Please try again.");
}

export async function removeLogo(): Promise<void> {
  const response = await fetch("/api/branding/logo", { method: "DELETE" });
  if (!response.ok) throw new Error("That did not work. Please try again.");
}

/**
 * One document, as Xero holds it, for the sheet this app prints itself.
 *
 * Shaped exactly as Xero returns it rather than flattened, so the print layout
 * reads the same fields whether the document came from Xero or from a fixture.
 */
export async function loadDocumentDetail(
  kind: string,
  id: string,
  source: DataSource,
): Promise<{ organisation: string; document: Record<string, unknown> }> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    return fixtures.DOCUMENT_DETAIL as { organisation: string; document: Record<string, unknown> };
  }
  return getJson(`/api/documents/${kind}/${id}`);
}

const NO_FOOTER: BrandingFooter = { email: '', phone: '', address: '' };

/** Everything a printed sheet needs from the branding record. */
export async function loadPrintBranding(
  source: DataSource,
): Promise<{ logoUrl: string | null; footer: BrandingFooter; presentation: Presentation }> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    // The same record the admin screen edits, so what is saved there is what
    // prints here during development.
    return {
      logoUrl: fixtures.BRANDING.logoUrl,
      footer: fixtures.BRANDING.footer,
      presentation: fixtures.BRANDING.presentation,
    };
  }
  try {
    const branding = await getJson<{
      logoUrl?: string | null;
      footer?: BrandingFooter;
      presentation?: Presentation;
    }>('/api/branding');
    return {
      logoUrl: branding.logoUrl ?? null,
      footer: branding.footer ?? NO_FOOTER,
      presentation: branding.presentation ?? EMPTY_PRESENTATION,
    };
  } catch {
    // Missing branding must not stop the document printing.
    return { logoUrl: null, footer: NO_FOOTER, presentation: EMPTY_PRESENTATION };
  }
}


/* ---------------------------------------------------------------------------
   Which ways of paying a company uses. CFOAI sets this; it decides what the
   payment form offers.
--------------------------------------------------------------------------- */

export interface PaymentMethodSettings {
  organisation: string;
  catalogue: PaymentMethodOption[];
  chosen: string[];
  defaultMethod: string | null;
}

export async function loadPaymentMethods(source: DataSource): Promise<PaymentMethodSettings> {
  if (import.meta.env.DEV && source === "local-fixtures") return fixtures.PAYMENT_METHODS;
  return getJson<PaymentMethodSettings>("/api/payment-methods");
}

export async function savePaymentMethods(
  chosen: string[],
  defaultMethod: string | null,
  source: DataSource,
): Promise<void> {
  if (import.meta.env.DEV && source === "local-fixtures") {
    fixtures.PAYMENT_METHODS.chosen = chosen;
    fixtures.PAYMENT_METHODS.defaultMethod = defaultMethod;
    return;
  }
  const response = await fetch("/api/payment-methods", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ chosen, defaultMethod }),
  });
  if (response.ok) return;
  const body = (await response.json().catch(() => null)) as { error?: string } | null;
  if (body?.error === "at_least_one_method") {
    throw new Error("Choose at least one way of being paid.");
  }
  throw new Error("That did not work. Please try again.");
}

/** Where a payment can go and how it arrived, without the list of invoices. */
export interface PaymentAccounts {
  /** Where a payment may go. Xero's bank accounts, plus any account
   *  explicitly set to receive payments. 'type' is Xero's own. */
  accounts: { code: string; name: string; type?: string }[];
  methods: PaymentMethodOption[];
  defaultMethod: string | null;
  currency: string;
}

export async function loadPaymentAccounts(source: DataSource): Promise<PaymentAccounts> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    return {
      accounts: fixtures.PAYMENT_OPTIONS.accounts,
      methods: fixtures.PAYMENT_OPTIONS.methods,
      defaultMethod: fixtures.PAYMENT_OPTIONS.defaultMethod,
      currency: fixtures.PAYMENT_OPTIONS.currency,
    };
  }
  return getJson<PaymentAccounts>('/api/payments/accounts');
}

/* ---------------------------------------------------------------------------
   Importing a list of contacts from a file.

   Three steps, because the middle one is the point: read the file, agree what
   it says, then send it.
--------------------------------------------------------------------------- */

export interface ContactImportPreview {
  headings: string[];
  columns: Record<string, number>;
  encoding: string | null;
  raw: string[][];
}

export interface CheckedImportRow {
  line: number;
  fields: Record<string, string>;
  problems: string[];
}

export interface ContactImportResult {
  created: { name: string; id: string }[];
  refused: { name: string; reason: string }[];
}

const IMPORT_ERRORS: Record<string, string> = {
  empty_file: 'That file is empty.',
  too_large: 'That file is too large. The limit is 5 MB.',
  could_not_read_file: 'That file could not be read. Save it as .xlsx or CSV and try again.',
  no_rows: 'That file has no rows under its headings.',
  too_many_rows: 'That is more than 2000 rows. Split it and import in parts.',
  no_name_column: 'No column looks like a contact name. Add one called Name, or use the template.',
};

async function importFailure(response: Response): Promise<Error> {
  const body = (await response.json().catch(() => null)) as { error?: string } | null;
  return new Error(IMPORT_ERRORS[body?.error ?? ''] ?? 'That did not work. Please try again.');
}

export async function previewContactImport(file: File, source: DataSource): Promise<ContactImportPreview> {
  if (import.meta.env.DEV && source === 'local-fixtures') return fixtures.CONTACT_IMPORT_PREVIEW;
  const response = await fetch('/api/contacts/import/preview', {
    method: 'POST',
    headers: { 'X-File-Name': encodeURIComponent(file.name).slice(0, 255) },
    body: file,
  });
  if (!response.ok) throw await importFailure(response);
  return (await response.json()) as ContactImportPreview;
}

export async function checkContactImport(
  raw: string[][],
  columns: Record<string, number>,
  source: DataSource,
): Promise<CheckedImportRow[]> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    return fixtures.checkImportLocally(raw, columns);
  }
  const response = await fetch('/api/contacts/import/check', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ raw, columns }),
  });
  if (!response.ok) throw await importFailure(response);
  return ((await response.json()) as { rows: CheckedImportRow[] }).rows;
}

export async function commitContactImport(
  rows: Record<string, string>[],
  source: DataSource,
): Promise<ContactImportResult> {
  if (import.meta.env.DEV && source === 'local-fixtures') {
    return { created: rows.map((r) => ({ name: r.name ?? '', id: 'dev' })), refused: [] };
  }
  const response = await fetch('/api/contacts/import', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rows }),
  });
  if (!response.ok) throw await importFailure(response);
  return (await response.json()) as ContactImportResult;
}
